// Money · Booked vs Projected vs Actual (Loom #3) — one labeled view that lines
// up, per month, the three revenue series that were previously scattered:
//   Booked    = on the books (reserved) by check-in month  [airroi booked_monthly: realized + upcoming]
//   Projected = AirROI market expectation                  [airroi monthly.projected_gross]
//   Actual    = realized so far (stays completed)          [airroi booked_monthly.realized]
// Plus "expected to date" (the time-relative pace curve) and same-point-last-year.
import { LoadingSpinner } from '../shared/components';
import { emptyState } from '../shared/components/Narrative';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export class MoneyTracking {
  private container: HTMLElement;
  private year = new Date().getUTCFullYear();
  private data: any = null;

  constructor(container: HTMLElement) { this.container = container; }

  private money(n: any): string {
    const v = Number(n);
    if (!Number.isFinite(v)) return '—';
    return '$' + Math.round(v).toLocaleString('en-US');
  }
  private moneyK(n: any): string {
    const v = Number(n); if (!Number.isFinite(v)) return '—';
    return Math.abs(v) >= 1e6 ? '$' + (v / 1e6).toFixed(2).replace(/\.?0+$/, '') + 'M' : Math.abs(v) >= 1e3 ? '$' + Math.round(v / 1e3) + 'k' : '$' + Math.round(v);
  }

  async render(): Promise<void> {
    this.container.innerHTML = `<div id="mt-body"><div style="padding:40px; text-align:center;">${LoadingSpinner.render()}</div></div>`;
    await this.load();
    this.draw();
  }

  private async load(): Promise<void> {
    try { this.data = await fetch(`/api/projections/airroi/${this.year}?scenario=expected`).then(r => r.ok ? r.json() : null); }
    catch { this.data = null; }
  }

  private draw(): void {
    const body = document.getElementById('mt-body');
    if (!body) return;
    const d = this.data;
    if (!d || d.available === false) {
      body.innerHTML = emptyState('No projection data for ' + this.year, 'AirROI projections are not available for this year yet.', '📈');
      this.bindYear();
      return;
    }
    const bookedBy = new Map<number, any>((d.booked_monthly || []).map((x: any) => [x.month, x]));
    const projBy = new Map<number, any>((d.monthly || []).map((x: any) => [x.month, x]));
    const stly: number[] = d.stly_monthly || [];
    const expByToday: number[] = d.expected_otb_by_today || [];
    const nowMonth = (this.year === new Date().getUTCFullYear()) ? (new Date().getUTCMonth() + 1) : 0;

    let tB = 0, tP = 0, tA = 0, tS = 0;
    const rows = MONTHS.map((mn, i) => {
      const m = i + 1;
      const bk = bookedBy.get(m) || { realized: 0, upcoming: 0 };
      const booked = (bk.realized || 0) + (bk.upcoming || 0);
      const actual = bk.realized || 0;
      const projected = (projBy.get(m) || {}).projected_gross || 0;
      const stlyV = stly[i] || 0;
      const exp = expByToday[i] || 0;
      tB += booked; tP += projected; tA += actual; tS += stlyV;
      const delta = booked - projected;
      const dCls = delta >= 0 ? 'mt-pos' : 'mt-neg';
      const cur = m === nowMonth;
      const pace = (m <= nowMonth && exp > 0) ? `${Math.round((booked / exp) * 100)}%` : '';
      return `<tr class="${cur ? 'mt-cur' : ''}">
        <td class="mt-mo">${mn}${cur ? ' <span class="mt-now">now</span>' : ''}</td>
        <td class="num">${this.money(booked)}</td>
        <td class="num mt-proj">${this.money(projected)}</td>
        <td class="num">${actual ? this.money(actual) : '—'}</td>
        <td class="num ${dCls}">${delta >= 0 ? '+' : ''}${this.money(delta)}</td>
        <td class="num mt-pace">${pace || '—'}</td>
        <td class="num mt-stly">${this.money(stlyV)}</td>
      </tr>`;
    }).join('');

    const kpi = (label: string, val: string, cap: string, tone = '') => `
      <div class="kpi-card ${tone}">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span></div>
        <div class="kpi-card-value">${val}</div>
        <div class="kpi-card-cap">${cap}</div>
      </div>`;
    const ytdPace = tP > 0 ? Math.round((tB / tP) * 100) : null;

    body.innerHTML = `
      <style>
        .mt-head { display:flex; align-items:center; gap:12px; margin-bottom:14px; flex-wrap:wrap; }
        .mt-head h3 { margin:0; font-size:15px; font-weight:600; }
        .mt-ynav { display:flex; align-items:center; gap:8px; margin-left:auto; font-size:13px; font-weight:600; }
        .mt-ynav button { padding:5px 11px; border:1px solid var(--border); border-radius:7px; background:var(--card); cursor:pointer; font-size:13px; }
        .mt-legend { font-size:11.5px; color:var(--text-muted); line-height:1.6; margin-bottom:14px; }
        .mt-legend b { color:var(--text); }
        table.mt-tbl { width:100%; border-collapse:collapse; font-size:12.5px; background:var(--card); border:1px solid var(--border); border-radius:var(--radius); overflow:hidden; }
        .mt-tbl th { font-size:10px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); text-align:right; padding:9px 14px; border-bottom:1px solid var(--border); white-space:nowrap; }
        .mt-tbl th:first-child { text-align:left; }
        .mt-tbl td { padding:8px 14px; border-bottom:1px solid var(--border-soft, #f1f5f9); }
        .mt-tbl td.num { text-align:right; font-variant-numeric:tabular-nums; }
        .mt-tbl tr:last-child td { border-bottom:none; }
        .mt-tbl .mt-mo { font-weight:600; }
        .mt-tbl .mt-proj { color:#3b82f6; }
        .mt-tbl .mt-stly, .mt-tbl .mt-pace { color:var(--text-faint, #9ca3af); }
        .mt-pos { color:#16a34a; } .mt-neg { color:#dc2626; }
        .mt-cur { background:#eaf2ff; }
        .mt-now { font-size:9px; font-weight:700; color:#1d4ed8; background:#dbeafe; padding:1px 5px; border-radius:4px; vertical-align:middle; }
        .mt-foot td { font-weight:700; border-top:2px solid var(--border); }
      </style>
      <div class="kpi-grid cols-3" style="margin-bottom:18px;">
        ${kpi('Booked (year)', this.moneyK(tB), 'on the books for ' + this.year)}
        ${kpi('Projected (year)', this.moneyK(tP), 'AirROI market expectation')}
        ${kpi('Actual realized', this.moneyK(tA), 'completed-stay reservation revenue', 'tone-good')}
        ${kpi('Pace to projection', ytdPace != null ? ytdPace + '%' : '—', 'booked ÷ projected, year', ytdPace != null && ytdPace >= 100 ? 'tone-good' : ytdPace != null ? 'tone-bad' : '')}
        ${kpi('vs last year', this.moneyK(tB - tS), 'booked vs same point STLY', (tB - tS) >= 0 ? 'tone-good' : 'tone-bad')}
      </div>
      <div class="mt-head">
        <h3>Booked vs Projected vs Actual</h3>
        <div class="mt-ynav">
          <button id="mt-prev">‹</button><span>${this.year}</span><button id="mt-next" ${this.year >= new Date().getUTCFullYear() + 1 ? 'disabled' : ''}>›</button>
        </div>
      </div>
      <div class="mt-legend">
        <b>Booked</b> = revenue on the books (reserved) by check-in month · <b>Projected</b> = AirROI <em>market expectation</em>, portfolio "expected" scenario (not tier-adjusted — the comp-tier view lives in Goals) · <b>Actual</b> = completed-stay reservation revenue (from bookings — no QuickBooks upload involved) · <b>Pace</b> = booked vs the expected-to-date curve · <b>STLY</b> = same point last year.<br>
        <b>Reading this vs Market &amp; Fee Types:</b> that tab's AirROI sections read the SAME projection at other scenarios (p25 / expected · avg / p90) and per property; its stored-projections table is a separate, manually generated set. When two "behind" figures differ, check which benchmark the table names before trusting either.
      </div>
      <table class="mt-tbl">
        <thead><tr><th>Month</th><th>Booked</th><th>Projected</th><th>Actual</th><th>vs Proj</th><th>Pace</th><th>STLY</th></tr></thead>
        <tbody>${rows}</tbody>
        <tfoot><tr class="mt-foot">
          <td class="mt-mo">${this.year} total</td>
          <td class="num">${this.money(tB)}</td>
          <td class="num mt-proj">${this.money(tP)}</td>
          <td class="num">${this.money(tA)}</td>
          <td class="num ${tB - tP >= 0 ? 'mt-pos' : 'mt-neg'}">${tB - tP >= 0 ? '+' : ''}${this.money(tB - tP)}</td>
          <td class="num">${ytdPace != null ? ytdPace + '%' : '—'}</td>
          <td class="num mt-stly">${this.money(tS)}</td>
        </tr></tfoot>
      </table>`;
    this.bindYear();
  }

  private bindYear(): void {
    document.getElementById('mt-prev')?.addEventListener('click', async () => { this.year--; await this.load(); this.draw(); });
    document.getElementById('mt-next')?.addEventListener('click', async () => { if (this.year < new Date().getUTCFullYear() + 1) { this.year++; await this.load(); this.draw(); } });
  }

  destroy(): void { /* no timers/listeners beyond DOM */ }
}
